import { useEffect, useRef, useState } from "react";
import { Loader2, FileText, Brain } from "lucide-react";
import { api } from "../../lib/api";

/**
 * Floating "server-side work in progress" indicator for the AppShell.
 *
 * Motivation: Founders often close their tab mid-analysis (which runs in the
 * background for 1-3 minutes) and come back wondering "did it work?" This
 * pill shows in the header on every founder page while:
 *   • any uploaded doc is still in the post-upload extraction phase
 *     (`upload_status: "pending"` or `vision_pending: true`)
 *   • an `analysis_jobs` row is `queued` or `running`
 *
 * Polls `/founder/processing-status` every 6s while there's work in flight,
 * every 60s otherwise (cheap query, ~1kb response). Auto-hides when idle.
 * Designed to be unobtrusive — small pill on the top-right of the header.
 *
 * Note: the S3 direct-upload phase (browser PUT bytes to S3) does NOT show
 * here because that phase is browser-driven and doesn't persist across tabs.
 * The sonner toast in Onboarding.jsx handles that in-session.
 */
export default function UploadProcessingIndicator({ userRole }) {
  const [state, setState] = useState({
    docs_processing: 0,
    docs_processing_names: [],
    analysis_active: false,
    analysis_started_at: null,
    analysis_kind: null,
  });
  const [expanded, setExpanded] = useState(false);
  // Ref-tracked "is there work?" so the scheduler can read the freshest
  // value without needing to be recreated on every state change (which was
  // causing one wasted poll cycle per state transition).
  const activeRef = useRef(false);
  activeRef.current = state.docs_processing > 0 || state.analysis_active;

  useEffect(() => {
    // Only founders have upload/analysis work to poll for.
    if (userRole !== "founder") return;
    let mounted = true;
    let pollHandle = null;

    const fetchStatus = async () => {
      try {
        const { data } = await api.get("/processing-status");
        if (mounted) setState(data);
      } catch (e) {
        // Silent — this is a background indicator, no need to noisy-log.
        if (process.env.NODE_ENV === "development") {
          console.warn("[processing-status] fetch failed", e);
        }
      }
    };

    // Adaptive polling: 6s while active, 60s when idle. Reads activeRef so
    // the recursive schedule() doesn't need to be recreated per state flip.
    const schedule = () => {
      const delay = activeRef.current ? 6000 : 60000;
      pollHandle = setTimeout(async () => {
        await fetchStatus();
        if (mounted) schedule();
      }, delay);
    };

    fetchStatus();
    schedule();
    // Immediate refresh when another view learns work just started (e.g. a
    // re-run answered "still reading your documents").
    const onRefresh = () => {
      if (pollHandle) clearTimeout(pollHandle);
      fetchStatus().then(() => { if (mounted) schedule(); });
    };
    window.addEventListener("clinq:processing-refresh", onRefresh);
    return () => {
      mounted = false;
      if (pollHandle) clearTimeout(pollHandle);
      window.removeEventListener("clinq:processing-refresh", onRefresh);
    };
  }, [userRole]);

  const hasWork = state.docs_processing > 0 || state.analysis_active;
  if (!hasWork) return null;

  // Build a friendly summary. Prioritise analysis phase since it's the
  // slower / more visible one.
  let label = "Working…";
  let sub = "";
  if (state.analysis_active) {
    label = "Analyzing your business";
    const started = state.analysis_started_at ? new Date(state.analysis_started_at) : null;
    const mins = started ? Math.max(0, Math.round((Date.now() - started.getTime()) / 60000)) : 0;
    sub = mins < 1
      ? "Just started — usually 1–3 minutes"
      : mins <= 3
        ? `Running for ${mins}m — nearly there`
        : `Running for ${mins}m — hang tight`;
  } else if (state.docs_processing > 0) {
    label = state.docs_processing === 1
      ? "Reading your document"
      : `Reading ${state.docs_processing} documents`;
    sub = "Extracting text · almost done";
  }

  return (
    <div
      data-testid="processing-indicator"
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
      onClick={() => setExpanded((v) => !v)}
      className="relative flex cursor-pointer items-center gap-2 rounded-full border border-[#00828F]/25 bg-[#E0F3F5] px-3 py-1.5 text-xs font-semibold text-[#004B62] shadow-sm transition hover:border-[#00828F]/40 hover:bg-[#A6DCE2]/40"
      role="status"
      aria-live="polite"
    >
      <Loader2 className="h-3.5 w-3.5 animate-spin text-[#00828F]" />
      <span className="hidden sm:inline">{label}</span>
      <span className="inline sm:hidden">Working</span>
      {/* Expanded tooltip on hover / tap */}
      {expanded && (
        <div
          data-testid="processing-indicator-tooltip"
          className="absolute right-0 top-full z-40 mt-2 w-72 rounded-xl border border-neutral-200 bg-white p-3 text-left text-xs shadow-xl"
        >
          {state.analysis_active && (
            <div className="flex items-start gap-2">
              <Brain className="mt-0.5 h-4 w-4 shrink-0 text-[#00828F]" />
              <div className="min-w-0">
                <p className="font-bold text-neutral-900">Analyzing your business</p>
                <p className="mt-0.5 text-neutral-600">{sub}</p>
              </div>
            </div>
          )}
          {state.docs_processing > 0 && (
            <div className={`flex items-start gap-2 ${state.analysis_active ? "mt-3 border-t border-neutral-100 pt-3" : ""}`}>
              <FileText className="mt-0.5 h-4 w-4 shrink-0 text-[#00828F]" />
              <div className="min-w-0">
                <p className="font-bold text-neutral-900">
                  {state.docs_processing === 1 ? "1 document processing" : `${state.docs_processing} documents processing`}
                </p>
                {state.docs_processing_names.length > 0 && (
                  <ul className="mt-0.5 space-y-0.5 text-neutral-600">
                    {state.docs_processing_names.slice(0, 3).map((n, i) => (
                      <li key={i} className="truncate">• {n || "(untitled)"}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
          <p className="mt-3 border-t border-neutral-100 pt-2 text-[11px] leading-relaxed text-neutral-500">
            Safe to keep working elsewhere — we&rsquo;ll finish in the background.
          </p>
        </div>
      )}
    </div>
  );
}
